import React, { useEffect, useState, useMemo, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import { fetchHotelOffers, authenticateTourVisio } from "../../utils/Hote";
import starIcon from "../../assets/img/star.png";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { determineUserCity } from "../../utils/userLocation";

const fillProgress = keyframes`
  0%   { width: 0%; }
  100% { width: 100%; }
`;

const ProgressBarWrapper = styled.div`
  width: 100%;
  height: 5px;
  background: #e0e0e0;
  border-radius: 5px;
  overflow: hidden;
  margin-bottom: 20px;
`;

const ProgressBar = styled.div`
  height: 5px;
  background: #0056b3;
  width: 0%;
  animation: ${fillProgress} 3.5s linear forwards;
`;

const FeaturedHotelsWrapper = styled.section`
  padding: 40px 0;
  background-color: #f8f9fa;
`;

const TabButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  margin-top: 30px;
  margin-bottom: 30px;

  button {
    background: transparent;
    border: none;
    font-size: 18px;
    color: #003663;
    cursor: pointer;
    padding-bottom: 4px;
    border-bottom: 2px solid transparent;
    transition: all 0.2s ease-in-out;

    &.active {
      font-weight: bold;
      border-color: #003663;
    }
  }
`;

const HotelList = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 0 12px;
  }
`;

const HotelCard = styled.div`
  background: #fff;
  border-radius: 10px;
  box-shadow: 0px 4px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
  opacity: 0;
  animation: fadeIn 0.5s ease-in-out forwards;
  cursor: pointer;
  width: 100%;
  margin-bottom: 16px;

  &:hover {
    transform: scale(1.02);
  }

  @media (max-width: 768px) {
    margin: 0 auto 16px auto;
    max-width: 100%;
  }
`;

const HotelCardImage = styled.div`
  position: relative;
  width: 100%;
  height: 200px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PriceOverlay = styled.div`
  position: absolute;
  bottom: 15px;
  right: 15px;
  font-size: 20px;
  font-weight: bold;
  color: #fff;
  background: rgba(0, 0, 0, 0.3);
  padding: 5px 8px;
  border-radius: 4px;

  &::after {
    content: "";
    display: block;
    margin-top: 4px;
    width: 100%;
    height: 2px;
    background: #ffcc00;
  }
`;

const CityBadge = styled.div`
  position: absolute;
  bottom: 125px; /* Higher than before */
  left: -6px;   /* More to the left */
  background-color: #ffcc00;
  color: #003663;
  font-weight: bold;
  font-size: 20px;
  padding: 6px 14px 6px 12px;
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
  z-index: 3;

  &::before {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    height: 6px;
    width: 100%;
    background-color: #003663;
    z-index: -1;
    border-bottom-right-radius: 4px;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -6px;
    left: 0;
    width: 0;
    height: 0;
    border-left: 6px solid transparent;
    border-top: 6px solid #003663;
  }
`;

const StarsAndName = styled.div`
  padding: 15px;

  .rating-row {
    font-size: 16px;
    color: #f5a623;
    margin-bottom: 5px;
  }

  .hotel-name {
    font-size: 18px;
    font-weight: bold;
    color: #333;
    margin: 0;
  }
`;

const SpecialOfferBadge = styled.div`
  background-color: #ffcc00;
  color: #003663;
  font-weight: bold;
  font-size: 12px;
  padding: 4px 8px;
  border-radius: 4px;
  margin-top: 8px;
  display: inline-block;
`;

const generateStars = (rating = 0) => {
  const stars = Math.min(Math.max(Math.floor(rating || 0), 0), 5);
  return Array.from({ length: stars }).map((_, idx) => (
    <img
      key={idx}
      src={starIcon}
      alt="star"
      style={{ width: "16px", height: "16px", marginRight: "2px" }}
    />
  ));
};

// Add seasonal check for Chalkidiki - cached to prevent excessive calls
let chalkidikiSeasonCache = null;
let chalkidikiSeasonCacheDate = null;

const isChalkidikiSeason = () => {
  const currentDate = new Date();
  const currentDateString = currentDate.toDateString();
  
  // Use cached result if it's from the same day
  if (chalkidikiSeasonCache !== null && chalkidikiSeasonCacheDate === currentDateString) {
    return chalkidikiSeasonCache;
  }
  
  const currentYear = currentDate.getFullYear();
  
  // Define season dates
  const seasonStart = new Date(currentYear, 3, 15); // April 15th
  const seasonEnd = new Date(currentYear, 8, 15);   // September 15th
  
  // Check if current date is within season
  const isInSeason = currentDate >= seasonStart && currentDate <= seasonEnd;
  
  
  // Cache the result
  chalkidikiSeasonCache = isInSeason;
  chalkidikiSeasonCacheDate = currentDateString;
  
  return isInSeason;
};

// Create a function that returns the cached value
const getChalkidikiSeasonStatus = () => isChalkidikiSeason();

// Add Greek island seasonal offers with seasonal check
const CHALKIDIKI_OFFER = { 
  name: "Chalkidiki", 
  id: 10386,
  isAvailable: getChalkidikiSeasonStatus()
};

const ZAKYNTHOS_OFFER = { 
  name: "Zakynthos", 
  id: 10381,
  isAvailable: getChalkidikiSeasonStatus()
};

const MYKONOS_OFFER = { 
  name: "Mykonos", 
  id: 10383,
  isAvailable: getChalkidikiSeasonStatus()
};

const CORFU_OFFER = { 
  name: "Corfu", 
  id: 10384,
  isAvailable: getChalkidikiSeasonStatus()
};

// Debug utility for Chalkidiki
const debugChalkidiki = (userCity, tabGroups) => {
  const isInSeason = getChalkidikiSeasonStatus();
  const isInTabs = Object.values(tabGroups).some(cities => cities.includes("Chalkidiki"));
  
  return { isInSeason, isInTabs };
};

// Objekti me cityId-të për qytetet e përzgjedhura
const cityMapping = {
  "Tirana": 82212,
  "Prishtina": 82389,
  "Dortmund": 10323,
  "Memmingen": 10353,
  "Stuttgart": 10256,
  "Zurich": 12427,
  "Basel": 12323,
  "Geneva": 12335,
  "Vienna": 11623,
  "Milan": 10613,
  "London": 10462,
  "Rome": 10613 // Rome
};

// Qytetet fqinje për çdo qytet (për shfaqjen e ofertave relevante)
const nearbyDestinations = {
  "Tirana": ["Prishtina", "Milan", "Vienna"],
  "Prishtina": ["Tirana", "Vienna", "Milan"],
  "Dortmund": ["Stuttgart", "Memmingen", "Basel"],
  "Memmingen": ["Stuttgart", "Dortmund", "Zurich"],
  "Stuttgart": ["Memmingen", "Dortmund", "Zurich"],
  "Zurich": ["Basel", "Geneva", "Stuttgart"],
  "Basel": ["Zurich", "Geneva", "Stuttgart"],
  "Geneva": ["Zurich", "Basel", "Milan"],
  "Vienna": ["Milan", "Tirana", "Prishtina"],
  "Milan": ["Tirana", "Vienna", "Geneva"],
  "London": ["Geneva", "Basel", "Zurich"],
  "Rome": ["Milan", "Vienna", "Tirana"]
};

// Default city nëse nuk gjendet lokacioni
const DEFAULT_CITY = "Prishtina";

// Oferta hotelesh të qyteteve të synuara për përdoruesit nga Prishtina
const targetCityMapping = {
  "Prishtina": [
    CHALKIDIKI_OFFER, // Add Greek islands as top priority seasonal offers
    ZAKYNTHOS_OFFER,
    MYKONOS_OFFER,
    CORFU_OFFER,
    { name: "Dortmund", id: 41128 },
    { name: "Memmingen", id: 16697 },
    { name: "Stuttgart", id: 40738 },
    { name: "Berlin", id: 41411 },
    { name: "Zurich", id: 15495 },
    { name: "Basel", id: 51121 },
    { name: "Geneva", id: 15545 },
    { name: "Vienna", id: 454590 },
    // Asia destinations
    { name: "Dubai", id: 10001 },
    { name: "Bangkok", id: 23113 },
    { name: "Seoul", id: 23310 },
    { name: "Tokyo", id: 19124 },
    // World destinations
    { name: "Cairo", id: 38429 },
    { name: "Buenos Aires", id: 11916 }
  ],
  // Për përdoruesit nga Tirana
  "Tirana": [
    CHALKIDIKI_OFFER, // Add Greek islands as top priority seasonal offers
    ZAKYNTHOS_OFFER,
    MYKONOS_OFFER,
    CORFU_OFFER,
    { name: "London", id: 1578 },
    { name: "Milan", id: 33912 },
    { name: "Rome", id: 26020 },
    { name: "Paris", id: 51446 },
    { name: "Bologna", id: 33808 },
    { name: "Vienna", id: 454590 },
    { name: "Dubai", id: 10001 },
    { name: "Abu Dhabi", id: 39940 }
  ],
  // Për përdoruesit nga Dortmund
  "Dortmund": [
    CHALKIDIKI_OFFER, // Add Greek islands as top priority seasonal offers
    ZAKYNTHOS_OFFER,
    MYKONOS_OFFER,
    CORFU_OFFER,
    { name: "Palma de Mallorca", id: 21437 },
    { name: "Bucharest", id: 10750 },
    { name: "Sofia", id: 16625 },
    { name: "Vienna", id: 454590 },
    { name: "Skopje", id: 51228 },
    { name: "Istanbul", id: 23472 },
    { name: "Tirana", id: 52001 },
    { name: "Thessaloniki", id: 26349 }
  ],
  // Për përdoruesit nga Memmingen
  "Memmingen": [
    CHALKIDIKI_OFFER, // Add Greek islands as top priority seasonal offers
    ZAKYNTHOS_OFFER,
    MYKONOS_OFFER,
    CORFU_OFFER,
    { name: "Palma de Mallorca", id: 21437 },
    { name: "Sofia", id: 16625 },
    { name: "Skopje", id: 51228 },
    { name: "Tirana", id: 52001 },
    { name: "Vienna", id: 454590 },
    { name: "Varna", id: 23202 },
    { name: "London", id: 1578 },
    { name: "Dublin", id: 18970 }
  ],
  // Për përdoruesit nga Stuttgart
  "Stuttgart": [
    CHALKIDIKI_OFFER, // Add Greek islands as top priority seasonal offers
    ZAKYNTHOS_OFFER,
    MYKONOS_OFFER,
    CORFU_OFFER,
    { name: "Palma de Mallorca", id: 21437 },
    { name: "Antalya", id: 23494 },
    { name: "Istanbul", id: 23472 },
    { name: "Berlin", id: 41411 },
    { name: "Hamburg", id: 41692 },
    { name: "Heraklion", id: 35104 },
    { name: "Amsterdam", id: 19311 }
  ],
  // Për përdoruesit nga Berlin
  "Berlin": [
    CHALKIDIKI_OFFER, // Add Greek islands as top priority seasonal offers
    ZAKYNTHOS_OFFER,
    MYKONOS_OFFER,
    CORFU_OFFER,
    { name: "Amsterdam", id: 19311 },
    { name: "Zurich", id: 15495 },
    { name: "Paris", id: 51446 },
    { name: "Istanbul", id: 23472 },
    { name: "Vienna", id: 454590 },
    { name: "Barcelona", id: 20877 },
    { name: "London", id: 1578 },
    { name: "Madrid", id: 52153 }
  ],
  // Për përdoruesit nga Zurich
  "Zurich": [
    CHALKIDIKI_OFFER, // Add Greek islands as top priority seasonal offers
    ZAKYNTHOS_OFFER,
    MYKONOS_OFFER,
    CORFU_OFFER,
    { name: "London", id: 1578 },
    { name: "New York", id: 29778 },
    { name: "Dubai", id: 10001 },
    { name: "Frankfurt", id: 17713 },
    { name: "Paris", id: 51446 },
    { name: "Amsterdam", id: 19311 },
    { name: "Istanbul", id: 23472 },
    { name: "Vienna", id: 454590 },
    { name: "Barcelona", id: 20877 },
    { name: "Prishtina", id: 82389 }
  ],
  // Për përdoruesit nga Basel
  "Basel": [
    CHALKIDIKI_OFFER, // Add Greek islands as top priority seasonal offers
    ZAKYNTHOS_OFFER,
    MYKONOS_OFFER,
    CORFU_OFFER,
    { name: "Palma de Mallorca", id: 21437 },
    { name: "London", id: 1578 },
    { name: "Istanbul", id: 23472 },
    { name: "Amsterdam", id: 19311 },
    { name: "Budapest", id: 18943 },
    { name: "Porto", id: 19882 },
    { name: "Barcelona", id: 20877 },
    { name: "Paris", id: 51446 },
    { name: "Frankfurt", id: 17713 }
  ],
  // Për përdoruesit nga Geneva
  "Geneva": [
    CHALKIDIKI_OFFER, // Add Greek islands as top priority seasonal offers
    ZAKYNTHOS_OFFER,
    MYKONOS_OFFER,
    CORFU_OFFER,
    { name: "Palma de Mallorca", id: 21437 },
    { name: "London", id: 1578 },
    { name: "Istanbul", id: 23472 },
    { name: "Amsterdam", id: 19311 },
    { name: "Budapest", id: 18943 },
    { name: "Porto", id: 19882 },
    { name: "Barcelona", id: 20877 },
    { name: "Paris", id: 51446 },
    { name: "Rome", id: 20316 },
  ],
  // Për përdoruesit nga Vienna
  "Vienna": [
    CHALKIDIKI_OFFER, // Add Greek islands as top priority seasonal offers
    ZAKYNTHOS_OFFER,
    MYKONOS_OFFER,
    CORFU_OFFER,
    { name: "Antalya", id: 23494 },
    { name: "Barcelona", id: 20877 },
    { name: "Palma de Mallorca", id: 21437 },
    { name: "Bangkok", id: 23113 },
    { name: "New York", id: 29778 },
    { name: "Frankfurt", id: 17713 },
    { name: "London", id: 1578 },
    { name: "Istanbul", id: 23472 },
    { name: "Amsterdam", id: 19311 }
  ],
  // Për përdoruesit nga Milan
  "Milan": [
    CHALKIDIKI_OFFER, // Add Greek islands as top priority seasonal offers
    ZAKYNTHOS_OFFER,
    MYKONOS_OFFER,
    CORFU_OFFER,
    { name: "Barcelona", id: 20877 },
    { name: "Paris", id: 51446 },
    { name: "Madrid", id: 52153 },
    { name: "Lisbon", id: 20107 },
    { name: "Athens", id: 26293 },
    { name: "Thessaloniki", id: 26349 },
    { name: "Amsterdam", id: 19311 },
    { name: "Vienna", id: 454590 },
    { name: "Frankfurt", id: 17713 },
    { name: "Tirana", id: 52001 }
  ],
  // Për përdoruesit nga London
  "London": [
    CHALKIDIKI_OFFER, // Add Greek islands as top priority seasonal offers
    ZAKYNTHOS_OFFER,
    MYKONOS_OFFER,
    CORFU_OFFER,
    { name: "New York", id: 29778 },
    { name: "Dubai", id: 10001 },
    { name: "Doha", id: 27422 },
    { name: "Dublin", id: 18970 },
    { name: "Los Angeles", id: 32797 },
    { name: "Madrid", id: 52153 },
    { name: "Singapore", id: 25709 },
    { name: "Amsterdam", id: 19311 },
    { name: "New Delhi", id: 53239 },
    { name: "Frankfurt", id: 17713 }
  ],
  // Për përdoruesit nga Rome
  "Rome": [
    CHALKIDIKI_OFFER, // Add Greek islands as top priority seasonal offers
    ZAKYNTHOS_OFFER,
    MYKONOS_OFFER,
    CORFU_OFFER,
    { name: "London", id: 1578 },
    { name: "Bucharest", id: 10750 },
    { name: "Tirana", id: 52001 },
    { name: "Kraków", id: 25239 },
    { name: "Sofia", id: 16625 },
    { name: "Prague", id: 15588 },
    { name: "Warsaw", id: 25236 },
    { name: "Budapest", id: 18943 },
    { name: "Brussels", id: 16977 },
    { name: "Dublin", id: 18970 }
  ]
};

// Grupimi i vendeve për të përcaktuar qytetin default
const countryToCityMapping = {
  "Albania": "Tirana",
  "Kosovo": "Prishtina",
  "Republic of Kosovo": "Prishtina",
  "Germany": "Dortmund",
  "Switzerland": "Zurich",
  "Austria": "Vienna",
  "Italy": "Milan",
  "United Kingdom": "London"
};

// konstante për ruajtjen e cache
const CACHE_KEY = "featuredHotelsCache";
const CACHE_EXPIRY = 60 * 60 * 1000; // 1 orë në milisekonda

// Define the fixed tab groups for Prishtina
const PRISHTINA_TAB_GROUPS = {
  Popular: [
    "Chalkidiki",
    "Zakynthos",
    "Mykonos",
    "Corfu",
    "Dortmund",
    "Memmingen", 
    "Stuttgart",
    "Berlin",
    "Zurich",
    "Basel",
    "Geneva",
    "Vienna"
  ],
  Europe: [
    "Vienna",
    "Milan",
    "Zurich",
    "Basel",
    "Dortmund",
    "Geneva",
    "Stuttgart",
    "Berlin",
    "Rome",
    "Lisbon"
  ],
  Asia: [
    "Seoul",
    "Tokyo",
    "Bangkok",
    "Dubai",
    "Singapore",
    "Kuala Lumpur"
  ],
  World: [
    "London",
    "Paris",
    "Rome",
    "New York",
    "Cairo",
    "Buenos Aires",
    "Bangkok"
  ]
};

// Define static tab groups that remain consistent regardless of user location
const STATIC_TAB_GROUPS = {
  World: [
    "London",
    "Paris", 
    "Rome",
    "New York",
    "Cairo",
    "Buenos Aires",
    "Bangkok",
    "Tokyo"
  ],
  Asia: [
    "Seoul",
    "Tokyo",
    "Bangkok",
    "Dubai",
    "Singapore",
    "Kuala Lumpur",
    "Jakarta",
    "Mumbai",
    
  ],
  Europe: [
    "Vienna",
    "Milan",
    "Zurich", 
    "Basel",
    "Dortmund",
    "Geneva",
    "Stuttgart",
    "Berlin",
    "Rome",
    "Lisbon",
    "Madrid",
    "Amsterdam",
    "Prague",
    "Budapest",
    "Chalkidiki",
    "Zakynthos",
    "Mykonos",
    "Corfu"
  ]
};

// City ID mappings for all static destinations
const STATIC_CITY_MAPPING = {
  // World cities
  "London": { name: "London", id: 1578 },
  "Paris": { name: "Paris", id: 51446 },
  "Rome": { name: "Rome", id: 26020 },
  "New York": { name: "New York", id: 29778 },
  "Cairo": { name: "Cairo", id: 38429 },
  "Buenos Aires": { name: "Buenos Aires", id: 11916 },
  
  // Asia cities
  "Seoul": { name: "Seoul", id: 23310 },
  "Tokyo": { name: "Tokyo", id: 19124 },
  "Bangkok": { name: "Bangkok", id: 23113 },
  "Dubai": { name: "Dubai", id: 10001 },
  "Singapore": { name: "Singapore", id: 25709 },
  "Kuala Lumpur": { name: "Kuala Lumpur", id: 27115 },
  "Jakarta": { name: "Jakarta", id: 24103 },
  "Mumbai": { name: "Mumbai", id: 53239 },
  
  // Europe cities
  "Vienna": { name: "Vienna", id: 454590 },
  "Milan": { name: "Milan", id: 33912 },
  "Zurich": { name: "Zurich", id: 15495 },
  "Basel": { name: "Basel", id: 51121 },
  "Dortmund": { name: "Dortmund", id: 41128 },
  "Geneva": { name: "Geneva", id: 15545 },
  "Stuttgart": { name: "Stuttgart", id: 40738 },
  "Berlin": { name: "Berlin", id: 41411 },
  "Lisbon": { name: "Lisbon", id: 20107 },
  "Madrid": { name: "Madrid", id: 52153 },
  "Amsterdam": { name: "Amsterdam", id: 19311 },
  "Prague": { name: "Prague", id: 15588 },
  "Budapest": { name: "Budapest", id: 18943 }
};

// Add enhanced caching configuration for featured hotels
const FEATURED_HOTELS_CACHE_KEY = "featuredHotelsCache";
const CACHE_EXPIRY_HOURS = 0.3;          // 0.3 h = 18 minutes for Popular / Europe tabs
const STATIC_TAB_EXPIRY_MINUTES = 18;    // 18 minutes for World & Asia tabs
const MAX_CACHE_ENTRIES = 50; // Maximum number of cache entries per user city

// Enhanced cache utilities
const getFeaturedHotelsCacheKey = (userCity, tabName, cityName) => {
  return `${userCity}_${tabName}_${cityName}`.toLowerCase().replace(/[^a-z0-9_]/g, '');
};

const loadFeaturedHotelsCache = () => {
  try {
    const cached = localStorage.getItem(FEATURED_HOTELS_CACHE_KEY);
    if (!cached) return {};
    
    const parsedCache = JSON.parse(cached);
    const now = Date.now();
    
    // Clean expired entries
    const cleanedCache = {};
    Object.entries(parsedCache).forEach(([key, entry]) => {
      const isStaticShort = key.includes("_world_") || key.includes("_asia_");
      const expiryMs = isStaticShort ? STATIC_TAB_EXPIRY_MINUTES * 60 * 1000 : CACHE_EXPIRY_HOURS * 60 * 60 * 1000;
      if (entry.timestamp && (now - entry.timestamp) < expiryMs) {
        cleanedCache[key] = entry;
      }
    });
    
    // If cache was cleaned, save it back
    if (Object.keys(cleanedCache).length !== Object.keys(parsedCache).length) {
      saveFeaturedHotelsCache(cleanedCache);
    }
    
    return cleanedCache;
  } catch (error) {
    console.error("Error loading featured hotels cache:", error);
    return {};
  }
};

const saveFeaturedHotelsCache = (cache) => {
  try {
    // Limit cache size by removing oldest entries
    const entries = Object.entries(cache);
    if (entries.length > MAX_CACHE_ENTRIES) {
      entries.sort((a, b) => (b[1].timestamp || 0) - (a[1].timestamp || 0));
      const limitedCache = {};
      entries.slice(0, MAX_CACHE_ENTRIES).forEach(([key, value]) => {
        limitedCache[key] = value;
      });
      localStorage.setItem(FEATURED_HOTELS_CACHE_KEY, JSON.stringify(limitedCache));
    } else {
      localStorage.setItem(FEATURED_HOTELS_CACHE_KEY, JSON.stringify(cache));
    }
  } catch (error) {
    console.error("Error saving featured hotels cache:", error);
    // If localStorage is full, clear the cache and try again
    try {
      localStorage.removeItem(FEATURED_HOTELS_CACHE_KEY);
      localStorage.setItem(FEATURED_HOTELS_CACHE_KEY, JSON.stringify(cache));
    } catch (e) {
      console.error("Failed to save featured hotels cache even after clearing:", e);
    }
  }
};

const getCachedFeaturedHotels = (userCity, tabName, cityName) => {
  const cache = loadFeaturedHotelsCache();
  const cacheKey = getFeaturedHotelsCacheKey(userCity, tabName, cityName);
  
  if (cache[cacheKey]) {
    return cache[cacheKey].data;
  }
  
  return null;
};

const cacheFeaturedHotels = (userCity, tabName, cityName, hotelData) => {
  if (!hotelData || !Array.isArray(hotelData) || hotelData.length === 0) {
    return;
  }
  
  const cache = loadFeaturedHotelsCache();
  const cacheKey = getFeaturedHotelsCacheKey(userCity, tabName, cityName);
  
  cache[cacheKey] = {
    data: hotelData,
    timestamp: Date.now()
  };
  
  saveFeaturedHotelsCache(cache);
};

// Utility function to clear featured hotels cache
const clearFeaturedHotelsCache = () => {
  try {
    localStorage.removeItem(FEATURED_HOTELS_CACHE_KEY);
  } catch (error) {
    console.error("Error clearing featured hotels cache:", error);
  }
};

const FeaturedHotels = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("Popular");
  const [allTabHotels, setAllTabHotels] = useState({});
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [userCity, setUserCity] = useState(DEFAULT_CITY);
  const [userCityLoaded, setUserCityLoaded] = useState(false);
  const [isFetchingHotels, setIsFetchingHotels] = useState(false); // Prevent multiple calls
  const navigate = useNavigate();

  // Util that wraps determineUserCity to preserve previous signature
  const getUserLocation = () =>
    determineUserCity({
      nearbyDestinations,
      countryToCityMapping,
      defaultCity: DEFAULT_CITY,
    });

  // Funksioni për të gjetur qytetin më të afërt nga lista jonë
  const findNearestCity = (userCity, countryName) => {
    // Listë e thjeshtë e qyteteve të disponueshme
    const availableCities = Object.keys(nearbyDestinations);
    
    // Nëse qyteti i përdoruesit gjendet në listën tonë
    if (availableCities.some(city => city.toLowerCase() === userCity.toLowerCase())) {
      return availableCities.find(city => city.toLowerCase() === userCity.toLowerCase());
    }
    
    // Nëse jo, përcaktojmë qytetin sipas vendit
    if (countryName && countryToCityMapping[countryName]) {
      return countryToCityMapping[countryName];
    }
    
    // Nëse nuk gjendet përshtatje, kthejmë qytetin default
    return DEFAULT_CITY;
  };

  // Funksioni për të marrë të dhënat e hoteleve sipas qytetit
  const fetchHotelsForCity = async (cityName, isPopularTab = false, isWorldTab = false, isAsiaTab = false) => {
    try {
      
      // Check cache first
      const cacheUserCity = isPopularTab ? userCity : 'static';
      const tabName = isPopularTab ? 'Popular' : isWorldTab ? 'World' : isAsiaTab ? 'Asia' : 'Europe';
      const cachedData = getCachedFeaturedHotels(cacheUserCity, tabName, cityName);
      if (cachedData) {
        return cachedData;
      }
      
      // Special handling for Greek islands (seasonal offers)
      const greekIslandOffers = {
        "Chalkidiki": CHALKIDIKI_OFFER,
        "Zakynthos": ZAKYNTHOS_OFFER,
        "Mykonos": MYKONOS_OFFER,
        "Corfu": CORFU_OFFER
      };

      if (greekIslandOffers[cityName]) {
        // Check if Greek island offers are in season (dynamically re-evaluate)
        const isCurrentlyInSeason = getChalkidikiSeasonStatus();
        if (!isCurrentlyInSeason) {
          return [];
        }

        try {
          const token = await authenticateTourVisio();
          
          // Calculate check-in date based on season
          const today = new Date();
          const checkInDate = moment(today).add(7, 'days');
          const checkOutDate = checkInDate.clone().add(2, 'days');
          const seasonEnd = new Date(today.getFullYear(), 8, 15); // September 15th
          
          
          // If we're near the end of season, adjust check-in date
          if (checkInDate.isAfter(moment(seasonEnd))) {
            return []; // Don't show offers if they would extend beyond season
          }

          const res = await fetchHotelOffers({ 
            cityId: greekIslandOffers[cityName].id,
            adults: 2,
            checkInDate: checkInDate.format('YYYY-MM-DD'),
            checkOutDate: checkOutDate.format('YYYY-MM-DD')
          });
          
          
          if (res?.hotels?.length > 0) {
;
            
            // Apply price markup and filter special offers
            const hotelsWithUpdatedPrices = res.hotels
              .map(hotel => ({
                ...hotel,
                offers: hotel.offers?.map(offer => ({
                  ...offer,
                  price: {
                    ...offer.price,
                    amount: +(offer.price.amount * 1.2).toFixed(2)
                  }
                }))
              }));

            // For Greek islands, prioritize special offers but include all hotels if no special offers exist
            const specialOfferHotels = hotelsWithUpdatedPrices.filter(h => h.badges?.some(b => b.id === "specialoffer"));
            const hotelsToShow = specialOfferHotels.length > 0 ? specialOfferHotels : hotelsWithUpdatedPrices;


            // Take top 3 offers for Greek islands
            const result = hotelsToShow.slice(0, 3).map(h => ({
              ...h,
              searchId: res.searchId,
              checkInDate: h.offers?.[0]?.checkIn,
              offerId: h.offers?.[0]?.offerId,
              isSpecialOffer: true,
              isSeasonalOffer: true,
              originalDestination: cityName // Store the original searched destination name
            }));

            // Cache the result
            const cacheUserCity = isPopularTab ? userCity : 'static';
            cacheFeaturedHotels(cacheUserCity, tabName, cityName, result);
            
            
            return result;
          }
        } catch (error) {
          console.error(`❌ Error fetching ${cityName} hotels:`, error);
          return [];
        }
      }

      // Regular city handling
      let cityTarget = null;
      
      // For Popular tab, use user-specific mapping
      if (isPopularTab) {
        cityTarget = targetCityMapping[userCity]?.find(
          (t) => t.name === cityName
        );
      } else {
        // For static tabs (Europe, Asia, World), use static mapping
        cityTarget = STATIC_CITY_MAPPING[cityName];
      }

      if (cityTarget) {
        
        try {
          const token = await authenticateTourVisio();
          
          const res = await fetchHotelOffers({ 
            cityId: cityTarget.id, 
            adults: 2,
            checkInDate: moment().add(7, 'days').format('YYYY-MM-DD'),
            checkOutDate: moment().add(9, 'days').format('YYYY-MM-DD')
          });
          
          if (res?.hotels?.length > 0) {
            
            // Apply price markup
            const hotelsWithUpdatedPrices = res.hotels.map(hotel => ({
              ...hotel,
              offers: hotel.offers?.map(offer => ({
                ...offer,
                price: {
                  ...offer.price,
                  amount: +(offer.price.amount * 1.2).toFixed(2)
                }
              }))
            }));

            // Filter special offers
            const specialHotels = hotelsWithUpdatedPrices.filter((h) =>
              h.badges?.some((b) => b.id === "specialoffer")
            );

            // For World tab, randomize selection
            if (isWorldTab) {
              let hotelsToShow = hotelsWithUpdatedPrices;
              for (let i = hotelsToShow.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [hotelsToShow[i], hotelsToShow[j]] = [hotelsToShow[j], hotelsToShow[i]];
              }
              const result = hotelsToShow.slice(0, 3).map((h) => ({
                ...h,
                searchId: res.searchId,
                checkInDate: h.offers?.[0]?.checkIn,
                offerId: h.offers?.[0]?.offerId,
              }));

              // Cache the result with static key for non-Popular tabs
              const cacheUserCity = isPopularTab ? userCity : 'static';
              cacheFeaturedHotels(cacheUserCity, 'World', cityName, result);
              return result;
            }

            // For Popular tab and others
            let hotelsToShow = specialHotels.length > 0 ? specialHotels : hotelsWithUpdatedPrices;
            hotelsToShow = hotelsToShow.sort((a, b) => 
              (a.offers?.[0]?.price?.amount || 0) - (b.offers?.[0]?.price?.amount || 0)
            );

            const limit = isPopularTab ? 10 : 2;
            const result = hotelsToShow.slice(0, limit).map((h) => ({
              ...h,
              searchId: res.searchId,
              checkInDate: h.offers?.[0]?.checkIn,
              offerId: h.offers?.[0]?.offerId,
            }));

            // Cache the result
            const cacheUserCity = isPopularTab ? userCity : 'static';
            cacheFeaturedHotels(cacheUserCity, tabName, cityName, result);
            
            return result;
          }
        } catch (error) {
          console.error(`❌ Error fetching hotels for ${cityName}:`, error);
          return [];
        }
      }
      
      return [];
    } catch (error) {
      console.error(`❌ Unexpected error in fetchHotelsForCity for ${cityName}:`, error);
      return [];
    }
  };

  // Update getTabGroups to use the fixed groups
  const getTabGroups = useMemo(() => {
    // Define base Popular destinations based on user location (only Popular changes)
    let popularDestinations = [];
    
    if (!userCityLoaded) {
      // Default to Prishtina destinations while loading
      popularDestinations = PRISHTINA_TAB_GROUPS.Popular.filter(city => 
        !["Chalkidiki", "Zakynthos", "Mykonos", "Corfu"].includes(city)
      );
    } else if (userCity === "Prishtina") {
      // Prishtina gets its specific destinations
      popularDestinations = PRISHTINA_TAB_GROUPS.Popular.filter(city => 
        !["Chalkidiki", "Zakynthos", "Mykonos", "Corfu"].includes(city)
      );
    } else {
      // For other cities, get destinations from targetCityMapping
      const destinations = targetCityMapping[userCity] || [];
      const europeDestinations = destinations
        .filter(city => 
          ["Vienna", "Milan", "Rome", "Paris", "London", "Bologna", "Zurich", "Basel", 
           "Geneva", "Dortmund", "Memmingen", "Stuttgart", "Berlin", "Amsterdam", 
           "Barcelona", "Madrid", "Frankfurt"].includes(city.name)
        )
        .map(city => city.name);
      
      popularDestinations = europeDestinations.slice(0, 8);
    }

    // Add Greek islands to popular destinations if in season (as top priority)
    const isGreekIslandsCurrentlyAvailable = getChalkidikiSeasonStatus();
    if (isGreekIslandsCurrentlyAvailable) {
      popularDestinations = ["Chalkidiki", "Zakynthos", "Mykonos", "Corfu", ...popularDestinations];
    }


    // Return tabs: Popular is dynamic, others are static
    return {
      Popular: popularDestinations,
      Europe: STATIC_TAB_GROUPS.Europe,
      Asia: STATIC_TAB_GROUPS.Asia,
      World: STATIC_TAB_GROUPS.World
    };
  }, [userCity, userCityLoaded]);

  const tabGroups = getTabGroups;
  const activeCityLabel = activeTab === "Popular" ? (userCity || DEFAULT_CITY) : activeTab;
  const hotels = allTabHotels[activeTab] || [];
  
  // Organize hotels for better display: Halkidiki first, then diverse cities
  const organizeHotelsForDisplay = (hotelList) => {
    if (!hotelList || hotelList.length === 0) return {
      halkidikiHotels: [],
      hotelsByCity: {},
      sortedCities: []
    };
    
    // Group hotels by city/destination
    const hotelsByCity = {};
    const halkidikiHotels = [];
    
    hotelList.forEach(hotel => {
      if (!hotel) return;
      
      const thumbnailUrl = hotel?.thumbnailFull || "";
      if (!thumbnailUrl || thumbnailUrl.trim() === "") return; // Skip hotels without images
      
      // Check if it's a Greek island seasonal offer
      const isGreekIslandOffer = hotel?.isSeasonalOffer === true;
      
      if (isGreekIslandOffer) {
        halkidikiHotels.push(hotel);
      } else {
        const cityName = hotel?.city?.name || 'Unknown';
        if (!hotelsByCity[cityName]) {
          hotelsByCity[cityName] = [];
        }
        hotelsByCity[cityName].push(hotel);
      }
    });
    
    // Sort Greek island hotels by price
    halkidikiHotels.sort((a, b) => 
      (a?.offers?.[0]?.price?.amount || 0) - (b?.offers?.[0]?.price?.amount || 0)
    );
    
    // Sort hotels in each city by price
    Object.keys(hotelsByCity).forEach(cityName => {
      hotelsByCity[cityName].sort((a, b) => 
        (a?.offers?.[0]?.price?.amount || 0) - (b?.offers?.[0]?.price?.amount || 0)
      );
    });
    
    // Get city names sorted by their cheapest hotel price
    const sortedCities = Object.keys(hotelsByCity).sort((cityA, cityB) => {
      const priceA = hotelsByCity[cityA][0]?.offers?.[0]?.price?.amount || 0;
      const priceB = hotelsByCity[cityB][0]?.offers?.[0]?.price?.amount || 0;
      return priceA - priceB;
    });
    
    
    return {
      halkidikiHotels,
      hotelsByCity,
      sortedCities
    };
  };
  
  const { halkidikiHotels = [], hotelsByCity = {}, sortedCities = [] } = organizeHotelsForDisplay(hotels);
  
  // Utility to shuffle array
  const shuffleArray = (arr) => arr.sort(() => Math.random() - 0.5);

  // Create the display array: 3 offers per Greek city, total 12 by default
  const createDisplayHotels = (showAll = false) => {
    const displayHotels = [];

    const safeGreekIslandHotels = Array.isArray(halkidikiHotels) ? halkidikiHotels : [];
    const safeSortedCities = Array.isArray(sortedCities) ? sortedCities : [];
    const safeHotelsByCity = hotelsByCity || {};

    const greekCitiesOrder = ["Chalkidiki", "Zakynthos", "Mykonos", "Corfu"];

    // Group Greek hotels by city and retain top 3 cheapest per city
    const greekIslandsByCity = {};
    safeGreekIslandHotels.forEach((hotel) => {
      const cityName = hotel.originalDestination || "Unknown";
      if (!greekIslandsByCity[cityName]) greekIslandsByCity[cityName] = [];
      greekIslandsByCity[cityName].push(hotel);
    });

    greekCitiesOrder.forEach((city) => {
      if (greekIslandsByCity[city]) {
        greekIslandsByCity[city].sort(
          (a, b) =>
            (a?.offers?.[0]?.price?.amount || 0) -
            (b?.offers?.[0]?.price?.amount || 0)
        );
        greekIslandsByCity[city] = greekIslandsByCity[city].slice(0, 3);
      }
    });

    // Build initial list (12 offers: 3 per Greek city)
    greekCitiesOrder.forEach((city) => {
      if (greekIslandsByCity[city]) {
        displayHotels.push(...greekIslandsByCity[city]);
      }
    });

    // Randomize order so offers are mixed
    let trimmed = shuffleArray(displayHotels);

    // Ensure we always have exactly 12 offers for default view (duplicates allowed if source < 12)
    if (!showAll && trimmed.length < 12) {
      const pool = [...trimmed];
      let idxAdd = 0;
      while (trimmed.length < 12 && pool.length > 0) {
        trimmed.push(pool[idxAdd % pool.length]);
        idxAdd += 1;
      }
    }

    if (showAll) {
      const MAX_PER_NON_GREEK_CITY = 3;
      // Include remaining non-Greek city hotels up to 3 per city beneath Greek offers
      safeSortedCities.forEach((cityName) => {
        const cityHotels = safeHotelsByCity[cityName] || [];
        trimmed.push(...cityHotels.slice(0, MAX_PER_NON_GREEK_CITY));
      });
      return shuffleArray(trimmed);
    }

    // Limit to 12 (all Greek) in default view
    return trimmed.slice(0, 12);
  };
  
  const displayHotels = createDisplayHotels(showAll);
  const totalAvailableHotels = (halkidikiHotels?.length || 0) + (sortedCities?.reduce((sum, city) => sum + (hotelsByCity[city]?.length || 0), 0) || 0);
  
  // Early return if no hotels are available and not loading
  if (!hotels || hotels.length === 0) {
    if (!progress && !isFetchingHotels) {
    }
  }
  
  // Filter hotels with valid images and log the filtering
  const hotelsWithImages = hotels.filter(hotel => {
    if (!hotel) return false;
    const thumbnailUrl = hotel?.thumbnailFull || "";
    return thumbnailUrl && thumbnailUrl.trim() !== "";
  });
  
  const filteredOutCount = hotels.length - hotelsWithImages.length;
  if (filteredOutCount > 0) {
  }

  // Debug Chalkidiki for troubleshooting (only log once when userCity changes)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && userCityLoaded) {
      debugChalkidiki(userCity, tabGroups);
    }
  }, [userCity, userCityLoaded, tabGroups]);

  // Funksioni për të kontrolluar cache dhe ngarkuar të dhënat nëse nevojitet
  const loadCachedData = () => {
    // Përditësim: Gjithmonë kthejmë false për të detyruar ngarkimin e të dhënave të reja
    return false;
    
    /*
    const cacheData = localStorage.getItem(CACHE_KEY);
    
    if (cacheData) {
      try {
        const { data, timestamp } = JSON.parse(cacheData);
        const now = new Date().getTime();
        
        // Kontrollo nëse të dhënat janë ende të vlefshme
        if (now - timestamp < CACHE_EXPIRY) {
          setAllTabHotels(data);
          setLoading(false);
          setProgress(false);
          return true;
        } else {
          return false;
        }
      } catch (error) {
        console.error('Error parsing cache:', error);
        return false;
      }
    }
    return false;
    */
  };

  // Funksioni për të ruajtur të dhënat në cache
  const saveToCache = (data) => {
    // Përditësim: Heqim përkohësisht ruajtjen në cache
    /*
    const cacheObj = {
      data,
      timestamp: new Date().getTime()
    };
    
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(cacheObj));
    } catch (error) {
      console.error('Error saving to cache:', error);
    }
    */
  };

  // Update fetchAllTabsHotels to only fetch for active tab (lazy loading)
  const fetchTabHotels = useCallback(async (targetTab = "Popular") => {
    // Prevent multiple simultaneous calls for the same tab
    if (isFetchingHotels) {
      return;
    }
    
    // Check if we already have data for this tab
    if (allTabHotels[targetTab] && allTabHotels[targetTab].length > 0) {
      return;
    }
    
    setIsFetchingHotels(true);
    setProgress(true);

    try {
      // Get user location if not loaded
      let currentUserCity = userCity;
      if (!userCityLoaded) {
        currentUserCity = await getUserLocation();
        setUserCity(currentUserCity);
        setUserCityLoaded(true);
      }

      // Get tab groups based on user city  
      const updatedTabGroups = getTabGroups;
      const cityNames = updatedTabGroups[targetTab] || [];
      

      // Initialize empty state for the target tab
      setAllTabHotels(prevState => ({
        ...prevState,
        [targetTab]: []
      }));

      // Function to update hotels for the target tab
      const updateTabHotels = (newHotels) => {
        setAllTabHotels(prevState => {
          const existingHotels = prevState[targetTab] || [];
          const updatedHotels = [...existingHotels, ...newHotels];
          // Sort by price for consistency
          const sortedHotels = updatedHotels.sort((a, b) => 
            ((a?.offers?.[0]?.price?.amount || 0) - (b?.offers?.[0]?.price?.amount || 0))
          );
          return {
            ...prevState,
            [targetTab]: sortedHotels
          };
        });
      };

      // Track completed requests to manage loading state
      let totalRequests = cityNames.length;
      let completedRequests = 0;

      // Function to check if all requests are complete
      const checkComplete = () => {
        completedRequests++;
        
        if (completedRequests >= totalRequests) {
          setProgress(false);
          setIsFetchingHotels(false);
        }
      };

      // Process each city in the target tab only
      cityNames.forEach(async (cityName) => {
        try {
          const hotelResults = await fetchHotelsForCity(
            cityName, 
            targetTab === "Popular",
            targetTab === "World",
            targetTab === "Asia"
          );
          
          if (hotelResults && Array.isArray(hotelResults) && hotelResults.length > 0) {
            updateTabHotels(hotelResults);
          } else {
          }
        } catch (error) {
          console.error(`❌ Error fetching hotels for ${cityName} in ${targetTab} tab:`, error);
        } finally {
          checkComplete();
        }
      });

      // Fallback to remove loading state after maximum wait time
      setTimeout(() => {
        if (isFetchingHotels) {
          setProgress(false);
          setIsFetchingHotels(false);
        }
      }, 15000); // 15 second timeout
    } catch (error) {
      console.error(`❌ Error in fetchTabHotels for ${targetTab}:`, error);
      setIsFetchingHotels(false);
      setProgress(false);
    }
  }, [getTabGroups, isFetchingHotels, allTabHotels, userCity, userCityLoaded]);

  useEffect(() => {
    // Only fetch hotels for the initial Popular tab on mount
    fetchTabHotels("Popular");
    
    // Add cache utilities to window object for debugging (development only)
    if (process.env.NODE_ENV === 'development') {
      window.featuredHotelsCache = {
        clear: clearFeaturedHotelsCache,
        stats: () => {
          const cache = loadFeaturedHotelsCache();
          return {
            totalEntries: Object.keys(cache).length,
            cacheSize: localStorage.getItem(FEATURED_HOTELS_CACHE_KEY)?.length || 0,
            entries: Object.keys(cache)
          };
        },
        view: loadFeaturedHotelsCache
      };
    }
  }, []); // Empty dependency array - only run once on mount

  // ─── Auto-refresh Featured Hotels Every 18 Minutes ──────────────────────
  useEffect(() => {
    const REFRESH_INTERVAL_MS = 18 * 60 * 1000; // 18 minutes

    const intervalId = setInterval(() => {
      // Clear local cache & state
      clearFeaturedHotelsCache();
      setAllTabHotels({});

      // Re-fetch the active tab so offers remain fresh (< 20m expiry)
      fetchTabHotels(activeTab);
    }, REFRESH_INTERVAL_MS);

    return () => clearInterval(intervalId);
  }, [activeTab, fetchTabHotels]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setShowAll(false);
    
    // Fetch hotels for the selected tab if we don't have them yet
    if (!allTabHotels[tab] || allTabHotels[tab].length === 0) {
      fetchTabHotels(tab);
    } else {
    }
  };

  return (
    <FeaturedHotelsWrapper className="my-5" style={{ maxWidth: "1400px", margin: "0 auto", padding: "0 32px" }}>
      <div style={{ textAlign: "center", marginBottom: 10 }}>
        <h3 style={{ color: "#003663" }}>
          <strong style={{ fontWeight: 700 }}>{t("popular")}</strong> {" "}
          <span style={{ color: "#003663" }}>{" "}from{" "}</span>
          <span style={{ color: "#003663" }}>{activeCityLabel}</span>
        </h3>
      </div>

      <TabButtons>
        {Object.keys(tabGroups || {}).map((tab) => (
          <button
            key={tab}
            onClick={() => handleTabChange(tab)}
            className={tab === activeTab ? "active" : ""}
          >
            {tab}
          </button>
        ))}
      </TabButtons>

      {progress && (
        <ProgressBarWrapper>
          <ProgressBar />
        </ProgressBarWrapper>
      )}

      {(!allTabHotels[activeTab] || allTabHotels[activeTab].length === 0) && !progress && (
        <div style={{ textAlign: "center", padding: "20px", color: "#666" }}>
          {isFetchingHotels ? "Loading hotels..." : "No hotels found for this selection."}
        </div>
      )}

      {(allTabHotels[activeTab] && allTabHotels[activeTab].length > 0 && hotelsWithImages.length === 0) && !progress && (
        <div style={{ textAlign: "center", padding: "20px", color: "#666" }}>
          Hotels found but none have images available. Please try another destination.
        </div>
      )}

      {displayHotels && Array.isArray(displayHotels) && displayHotels.length > 0 && (
        <>
          <HotelList>
            {(showAll ? displayHotels : displayHotels.slice(0, 12))
              .map((hotel, idx) => {
              if (!hotel) return null;

              const totalPrice = hotel?.offers?.[0]?.price?.amount || 0;
              const rating = hotel?.rating ?? hotel?.stars ?? 0;
              const rawCityName = hotel?.city?.name;
              
              // Special handling for Greek island offers - display the original searched destination name
              // Only for hotels marked as seasonal offers (which are Greek island specific)
              const isGreekIslandOffer = hotel?.isSeasonalOffer === true;
              
              // Use the stored original destination name for Greek island offers, otherwise use raw city name
              const cityName = isGreekIslandOffer && hotel?.originalDestination 
                ? hotel.originalDestination 
                : rawCityName;
              
              const hotelName = hotel?.name || "Unknown Hotel";
              const thumbnailUrl = hotel?.thumbnailFull || "";
              const searchId = hotel?.searchId;
              const offerId = hotel?.offerId;
              const checkInDate = hotel?.checkInDate;
              const checkOutDate = checkInDate ? 
                moment(checkInDate).add(hotel?.offers?.[0]?.night || 2, "days").format("YYYY-MM-DD") :
                null;

              return (
                <HotelCard
                  key={`${idx}-${hotel?.id || idx}`}
                  onClick={() => {
                    if (hotel?.id) {
                      navigate(`/hotels/details/${hotel.id}`, {
                        state: {
                          hotelId: hotel.id,
                          hotelSearchData: hotel,
                          searchId: searchId,
                          offerId: offerId,
                          checkInDate: checkInDate,
                          checkOutDate: checkOutDate,
                          address: hotel?.address || "",
                        },
                      });
                    }
                  }}
                >
                  <HotelCardImage>
                    <img 
                      src={thumbnailUrl || "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200' viewBox='0 0 300 200'%3E%3Crect width='300' height='200' fill='%23f8f9fa'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial, sans-serif' font-size='14' fill='%23666' text-anchor='middle' dy='.3em'%3ENo Image Available%3C/text%3E%3C/svg%3E"} 
                      alt={hotelName}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='300' height='200' viewBox='0 0 300 200'%3E%3Crect width='300' height='200' fill='%23f8f9fa'/%3E%3Ctext x='50%25' y='50%25' font-family='Arial, sans-serif' font-size='14' fill='%23666' text-anchor='middle' dy='.3em'%3EImage Not Available%3C/text%3E%3C/svg%3E";
                      }}
                    />
                    <PriceOverlay>{Math.round(totalPrice)}€</PriceOverlay>
                    {cityName && (
                      <CityBadge>{cityName}</CityBadge>
                    )}
                  </HotelCardImage>
                  <StarsAndName>
                    <div className="rating-row" style={{ display: "flex", alignItems: "center" }}>
                      {generateStars(rating)}
                    </div>
                    <h5 className="hotel-name" style={{ color: "#003663" }}>{hotelName}</h5>
                    {hotel?.badges?.some((badge) => badge?.id === "specialoffer") && (
                      <SpecialOfferBadge>{t("special_offer")}</SpecialOfferBadge>
                    )}
                  </StarsAndName>
                </HotelCard>
              );
            })}
          </HotelList>

          {!showAll && totalAvailableHotels > 12 && (
            <div style={{ textAlign: "center", marginTop: 20 }}>
              <button
                onClick={() => setShowAll(true)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#003663",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                Show more ({totalAvailableHotels - 12} more offers) ▼
              </button>
            </div>
          )}
        </>
      )}
    </FeaturedHotelsWrapper>
  );
};

export default FeaturedHotels;
